/**
 * HOOK: useEnterpriseTable
 *
 * Hook générique pour gérer toute la logique des tableaux Enterprise :
 * - Recherche globale (côté client OU serveur)
 * - Filtres avancés (côté client OU serveur)
 * - Tri (côté client OU serveur)
 * - Pagination (côté client OU serveur)
 * - Sélection multiple
 * - Visibilité des colonnes
 *
 * Supporte 2 modes :
 * - CLIENT-SIDE: toutes les données en mémoire, filtrage/tri/pagination client
 * - SERVER-SIDE: filtrage/tri/pagination déléguée au backend (future implémentation)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterCondition, FilterMode } from '../types/filters';

type ColumnVisibilityState = Record<string, boolean>;

export interface UseEnterpriseTableOptions<TData> {
  data: TData[];
  searchableFields?: (keyof TData)[];
  initialPageSize?: number;
  mode?: 'client' | 'server';
  initialFilters?: FilterCondition[];
  initialFilterMode?: FilterMode;
  initialColumnVisibility?: ColumnVisibilityState; // Visibilité initiale des colonnes

  // Paramètres pour le mode server
  total?: number; // Nombre total d'items (server-side)
  totalPages?: number; // Nombre total de pages (server-side)
  currentPage?: number; // Page courante (server-side)
  itemsPerPage?: number; // Items par page (server-side)
  onSearchChange?: (search: string) => void; // Callback pour recherche server-side
  onPageChange?: (page: number) => void; // Callback pour changement de page server-side
  onItemsPerPageChange?: (itemsPerPage: number) => void; // Callback pour changement items per page
  onFiltersChange?: (filters: FilterCondition[], filterMode: FilterMode) => void; // Callback pour filtres server-side
}

export interface UseEnterpriseTableReturn<TData> {
  // Données
  displayedData: TData[];
  filteredData: TData[];
  allData: TData[];

  // Recherche
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Filtres
  activeFilters: FilterCondition[];
  filterMode: FilterMode;
  setActiveFilters: (filters: FilterCondition[], mode?: FilterMode) => void;
  clearFilters: () => void;

  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;

  // Sélection (nouveau système avec IDs persistants)
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void; // Exposé pour sélection globale
  selectedCount: number;
  toggleItemSelection: (id: string) => void;
  toggleAllPageSelection: (items: TData[], select: boolean) => void;
  isItemSelected: (id: string) => boolean;
  isAllPageSelected: (items: TData[]) => boolean;
  isSomePageSelected: (items: TData[]) => boolean;
  clearSelection: () => void;
  clearSelectionTrigger: number;

  // Compatibilité avec l'ancien système (pour les pages qui utilisent selectedItems)
  selectedItems: TData[];
  setSelectedItems: (items: TData[]) => void;

  // Colonnes
  columnVisibility: ColumnVisibilityState;
  setColumnVisibility: (visibility: ColumnVisibilityState) => void;

  // Tri client-side
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  handleSortChange: (field: string | null, direction: 'asc' | 'desc') => void;

  // Stats
  totalItems: number;
  displayedItemsCount: number;
}

export function useEnterpriseTable<TData extends Record<string, any>>({
  data,
  searchableFields = [],
  initialPageSize = 25,
  mode = 'client',
  initialFilters = [],
  initialFilterMode = 'and',
  initialColumnVisibility = {},
  total: serverTotal,
  totalPages: serverTotalPages,
  currentPage: serverCurrentPage,
  itemsPerPage: serverItemsPerPage,
  onSearchChange,
  onPageChange,
  onItemsPerPageChange: onItemsPerPageChangeCallback,
  onFiltersChange,
}: UseEnterpriseTableOptions<TData>): UseEnterpriseTableReturn<TData> {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<FilterCondition[]>(initialFilters);
  const [filterMode, setFilterMode] = useState<FilterMode>(initialFilterMode);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(initialPageSize);
  // ✅ Nouveau système de sélection avec Set<string> persistant entre les pages
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(initialColumnVisibility);
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const isServerMode = mode === 'server';

  // Ref stable pour searchableFields — évite de recréer searchInFields à chaque render
  // quand l'appelant passe un littéral de tableau inline (ex: ['email', 'full_name'])
  const searchableFieldsRef = useRef(searchableFields);
  searchableFieldsRef.current = searchableFields;

  // ✅ FIX: Utiliser une ref pour éviter le double fetch au montage initial
  const isInitialMount = useRef(true);

  // En mode server, notifier les changements de filtres (sauf au montage initial)
  useEffect(() => {
    // Skip le premier render pour éviter le double fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isServerMode && onFiltersChange) {
      onFiltersChange(activeFilters, filterMode);
    }
  }, [activeFilters, filterMode, isServerMode, onFiltersChange]);

  // En mode server, utiliser les valeurs passées en props
  // En mode client, utiliser les states locaux
  const currentPage =
    isServerMode && serverCurrentPage !== undefined ? serverCurrentPage : localCurrentPage;
  const itemsPerPage =
    isServerMode && serverItemsPerPage !== undefined ? serverItemsPerPage : localItemsPerPage;

  // Fonction de recherche générique dans les champs spécifiés
  // Dépendance via ref uniquement : évite la boucle de rendu infinie quand l'appelant
  // passe un tableau littéral inline et qu'un tri est actif (filteredData crée un nouveau
  // tableau à chaque sort, ce qui déclencherait un cycle data → effect → setState → render)
  const searchInFields = useCallback(
    (item: TData, term: string): boolean => {
      const lowerTerm = term.toLowerCase();

      for (const field of searchableFieldsRef.current) {
        const value = item[field];

        if (value == null) continue;

        const stringValue = String(value).toLowerCase();
        if (stringValue.includes(lowerTerm)) return true;

        if (typeof value === 'string' && !isNaN(Date.parse(value))) {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('fr-FR');
          if (formattedDate.includes(lowerTerm)) return true;
        }
      }

      return false;
    },
    [] // Stable — lit toujours searchableFieldsRef.current
  );

  /**
   * Récupère une valeur imbriquée via dot notation (ex: "product.name")
   */
  const getNestedValue = useCallback((obj: TData, path: string): unknown => {
    return path.split('.').reduce((current: any, key) => {
      return current?.[key];
    }, obj);
  }, []);

  /**
   * Vérifie si un item correspond à un filtre donné
   */
  const matchesFilter = useCallback(
    (item: TData, filter: FilterCondition): boolean => {
      // Support des chemins imbriqués (ex: "product.name", "origin.label")
      const value = getNestedValue(item, filter.field);

      // Opérateurs qui testent la présence/absence de valeur — traiter avant le bail-out null
      if (filter.operator === 'isNull') return value == null || value === '';
      if (filter.operator === 'isNotNull') return value != null && value !== '';

      if (value == null) return false;

      const stringValue = String(value).toLowerCase();
      const filterValue = Array.isArray(filter.value)
        ? filter.value
        : String(filter.value).toLowerCase();

      switch (filter.operator) {
        case 'equals':
          // Pour les nombres, comparer numériquement
          if (typeof value === 'number') {
            const numFilterValue = Number(filter.value);
            return !isNaN(numFilterValue) && value === numFilterValue;
          }
          // Pour les dates ISO (ex: "2025-12-22T10:30:00Z"), comparer uniquement la partie date
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
            const itemDateStr = value.split('T')[0];
            const filterDateStr = String(filter.value).split('T')[0];
            return itemDateStr === filterDateStr;
          }
          return stringValue === filterValue;

        case 'notEquals':
          if (typeof value === 'number') {
            const numFilterValue = Number(filter.value);
            return !isNaN(numFilterValue) && value !== numFilterValue;
          }
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
            return value.split('T')[0] !== String(filter.value).split('T')[0];
          }
          return stringValue !== filterValue;

        case 'in': {
          // Comparaison insensible à la casse, comme `equals` juste au-dessus
          // (le chemin serveur, lui, est sensible à la casse via .in()). Écart
          // préexistant à ce chemin client, pas introduit ici.
          const members = Array.isArray(filter.value) ? filter.value : [filter.value];
          return members.some((member) => String(member).toLowerCase() === stringValue);
        }

        case 'sameDay': {
          if (!value) return false;
          const itemDay = String(value).split('T')[0];
          const filterDay = String(filter.value).split('T')[0];
          return itemDay === filterDay;
        }

        case 'greaterThan': {
          const numValue = Number(value);
          const numFilterValue = Number(filter.value);
          return !isNaN(numValue) && !isNaN(numFilterValue) && numValue > numFilterValue;
        }

        case 'lessThan': {
          const numValue = Number(value);
          const numFilterValue = Number(filter.value);
          return !isNaN(numValue) && !isNaN(numFilterValue) && numValue < numFilterValue;
        }

        case 'contains':
          return stringValue.includes(filterValue as string);

        case 'startsWith':
          return stringValue.startsWith(filterValue as string);

        case 'endsWith':
          return stringValue.endsWith(filterValue as string);

        case 'before': {
          const itemDate = new Date(value as string);
          const filterDate = new Date(filterValue as string);
          return itemDate < filterDate;
        }

        case 'after': {
          const itemDate = new Date(value as string);
          const filterDate = new Date(filterValue as string);
          return itemDate > filterDate;
        }

        case 'between': {
          if (!Array.isArray(filter.value) || filter.value.length !== 2) return false;
          const itemDate = new Date(value as string);
          const startDate = new Date(filter.value[0]);
          const endDate = new Date(filter.value[1]);
          return itemDate >= startDate && itemDate <= endDate;
        }

        default:
          return false;
      }
    },
    [getNestedValue]
  );

  /**
   * Applique les filtres selon le mode (AND ou OR)
   */
  const applyFilters = useCallback(
    (item: TData, filters: FilterCondition[], mode: FilterMode): boolean => {
      if (filters.length === 0) return true;

      if (mode === 'or') {
        // Mode OU : au moins un filtre doit correspondre
        return filters.some((filter) => matchesFilter(item, filter));
      } else {
        // Mode ET : tous les filtres doivent correspondre
        return filters.every((filter) => matchesFilter(item, filter));
      }
    },
    [matchesFilter]
  );

  // Filtrage par recherche ET filtres avancés
  const filteredData = useMemo(() => {
    if (isServerMode) return data; // En mode server, données déjà filtrées

    let result = data;

    // Appliquer la recherche globale (CLIENT-SIDE)
    // Multi-mots : chaque mot doit matcher au moins un champ (même sémantique
    // que buildSearchOrConditions côté serveur).
    if (searchTerm) {
      const words = searchTerm.split(/\s+/).filter(Boolean);
      result = result.filter((item) => words.every((word) => searchInFields(item, word)));
    }

    // Appliquer les filtres avancés (CLIENT-SIDE) avec le mode ET/OU
    if (activeFilters.length > 0) {
      result = result.filter((item) => applyFilters(item, activeFilters, filterMode));
    }

    // Tri client-side (avant pagination)
    if (sortField) {
      result = [...result].sort((a, b) => {
        const av = a[sortField] ?? '';
        const bv = b[sortField] ?? '';
        const cmp =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av) < String(bv)
              ? -1
              : String(av) > String(bv)
                ? 1
                : 0;
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [
    data,
    searchTerm,
    searchInFields,
    activeFilters,
    filterMode,
    applyFilters,
    isServerMode,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = isServerMode
    ? serverTotalPages || 0 // En mode server, utiliser la valeur fournie
    : Math.ceil(filteredData.length / itemsPerPage); // En mode client, calculer

  const displayedData = useMemo(() => {
    if (isServerMode) {
      // En mode serveur, les données sont déjà paginées et filtrées
      return data;
    }

    // En mode client, on pagine nous-mêmes
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage, isServerMode, data]);

  // Réinitialiser la page quand la recherche change
  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);

      if (isServerMode) {
        // En mode server, notifier le parent (il gérera le reset de page)
        if (onSearchChange) {
          onSearchChange(term);
        }
      } else {
        // En mode client, gérer localement
        setLocalCurrentPage(1);
      }
    },
    [isServerMode, onSearchChange]
  );

  // Réinitialiser la page quand les filtres changent
  const handleFiltersChange = useCallback(
    (filters: FilterCondition[], mode?: FilterMode) => {
      setActiveFilters(filters);
      if (mode !== undefined) {
        setFilterMode(mode);
      }

      if (!isServerMode) {
        setLocalCurrentPage(1); // Retour à la première page (client uniquement)
      }
    },
    [isServerMode]
  );

  // Handler pour changement de page
  const handlePageChange = useCallback(
    (page: number) => {
      if (isServerMode) {
        // En mode server, notifier le parent
        if (onPageChange) {
          onPageChange(page);
        }
      } else {
        // En mode client, gérer localement
        setLocalCurrentPage(page);
      }
    },
    [isServerMode, onPageChange]
  );

  // Réinitialiser la page quand le nombre d'items par page change
  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage: number) => {
      if (isServerMode) {
        // En mode server, notifier le parent (il gérera le reset de page)
        if (onItemsPerPageChangeCallback) {
          onItemsPerPageChangeCallback(newItemsPerPage);
        }
      } else {
        // En mode client, gérer localement
        setLocalItemsPerPage(newItemsPerPage);
        setLocalCurrentPage(1); // Reset page
      }
    },
    [isServerMode, onItemsPerPageChangeCallback]
  );

  // Clear filters
  const clearFilters = useCallback(() => {
    setActiveFilters([]);
    setFilterMode('and');
    if (!isServerMode) {
      setLocalCurrentPage(1);
    }
  }, [isServerMode]);

  // =========================================================================
  // SÉLECTION MULTI-PAGES (nouveau système avec Set<string>)
  // =========================================================================

  /**
   * Basculer la sélection d'un item par son ID
   */
  const toggleItemSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  /**
   * Sélectionner/désélectionner tous les items de la page courante
   */
  const toggleAllPageSelection = useCallback((items: TData[], select: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      items.forEach((item: any) => {
        if (select) {
          newSet.add(item.id);
        } else {
          newSet.delete(item.id);
        }
      });
      return newSet;
    });
  }, []);

  /**
   * Vérifier si un item est sélectionné
   */
  const isItemSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  /**
   * Vérifier si tous les items de la page sont sélectionnés
   */
  const isAllPageSelected = useCallback(
    (items: TData[]) => {
      if (items.length === 0) return false;
      return items.every((item: any) => selectedIds.has(item.id));
    },
    [selectedIds]
  );

  /**
   * Vérifier si certains (mais pas tous) items de la page sont sélectionnés
   */
  const isSomePageSelected = useCallback(
    (items: TData[]) => {
      if (items.length === 0) return false;
      const someSelected = items.some((item: any) => selectedIds.has(item.id));
      const allSelected = items.every((item: any) => selectedIds.has(item.id));
      return someSelected && !allSelected;
    },
    [selectedIds]
  );

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setClearSelectionTrigger(Date.now());
  }, []);

  const handleSortChange = useCallback((field: string | null, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  /**
   * Calculer selectedItems à partir des IDs sélectionnés et des données affichées
   * Note: En mode server, cela ne retourne que les items de la page courante
   * Pour avoir tous les items sélectionnés, utiliser getSelectedItems() de useEntityList
   */
  const selectedItems = useMemo(() => {
    return data.filter((item: any) => selectedIds.has(item.id));
  }, [data, selectedIds]);

  /**
   * Fonction de compatibilité pour setSelectedItems (ancien système)
   * Convertit un tableau d'items en Set d'IDs
   */
  const setSelectedItems = useCallback((items: TData[]) => {
    setSelectedIds(new Set(items.map((item: any) => item.id)));
  }, []);

  return {
    // Données
    displayedData,
    filteredData,
    allData: data,

    // Recherche
    searchTerm,
    setSearchTerm: handleSearchChange,

    // Filtres
    activeFilters,
    filterMode,
    setActiveFilters: handleFiltersChange,
    clearFilters,

    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    setCurrentPage: handlePageChange,
    setItemsPerPage: handleItemsPerPageChange,

    // Sélection (nouveau système avec IDs persistants)
    selectedIds,
    setSelectedIds, // Exposé pour sélection globale (Feature 4.4)
    selectedCount: selectedIds.size,
    toggleItemSelection,
    toggleAllPageSelection,
    isItemSelected,
    isAllPageSelected,
    isSomePageSelected,
    clearSelection,
    clearSelectionTrigger,

    // Compatibilité avec l'ancien système
    selectedItems,
    setSelectedItems,

    // Colonnes
    columnVisibility,
    setColumnVisibility,

    // Tri client-side
    sortField,
    sortDirection,
    handleSortChange,

    // Stats
    totalItems: isServerMode ? serverTotal || 0 : filteredData.length,
    displayedItemsCount: displayedData.length,
  };
}
